import {Component, HostListener, OnInit} from '@angular/core';
import {Sidenavaction} from 'src/assets/js/application/sidenavaction';
import * as _ from 'lodash';
import CustomerPondModel from 'src/app/services/customerPond.model';
import {CustomerPondService} from 'src/app/services/customerPond.service';
import {FarmSiteService} from 'src/app/services/farmSite.service';
import FarmSiteModel from 'src/app/services/farmSite.model';
import {FeedLogService} from 'src/app/services/feedLog.service';
import FeedLogModel from 'src/app/services/feedLog.model';
import {CommonMethodsService} from 'src/app/services/commonMethods.service';
import {LocationStrategy} from '@angular/common';
import {PreviousRouteService} from 'src/app/services/previousRouteService';
import {BatchLoader} from "../../../../Utils/batch-loader.util";

declare var $: any;

/**
 * @component TotalPondsListComponent
 * @selector app-total-ponds-list
 * @implements {OnInit}
 *
 * @description
 * The `TotalPondsListComponent` displays a comprehensive list of all ponds (total area)
 * across the farm business. It serves as a central dashboard view for monitoring pond status.
 *
 * ## Key Responsibilities:
 * 1. **Data Aggregation**: Fetches and aggregates pond data from all farm sites.
 * 2. **Filtering & Search**: Provides client-side filtering capabilities.
 * 3. **Sorting**: Allows users to sort the pond list.
 * 4. **Pagination**: Implements batch loading for large datasets.
 * 5. **Detail View**: Allows users to view detailed feed logs.
 *
 * ## User Roles:
 * - **Farmer/Business Admin**: Access to all ponds in their business.
 * - **Farm Manager**: Access to ponds in assigned farm sites.
 * - **Provider**: Access to ponds in associated farm sites.
 *
 * @service FarmSiteService
 * @service CustomerPondService
 * @service FeedLogService
 * @service CommonMethodsService
 */
@Component({
  selector: 'app-total-ponds-list',
  templateUrl: './total-ponds-list.component.html',
  styleUrls: ['./total-ponds-list.component.scss']
})
export class TotalPondsListComponent implements OnInit {
  /** @type {Array<FarmSiteModel>} List of farm sites. */
  public farmSites: Array<FarmSiteModel>;

  /** @type {FarmSiteModel} Single farm site model. */
  public farmSite: FarmSiteModel;

  /** @type {Array<CustomerPondModel>} List of customer ponds displayed. */
  public customerPonds: Array<CustomerPondModel>;

  /** @type {Array<CustomerPondModel>} Master list of customer ponds. */
  public customerPondsList: Array<CustomerPondModel>;

  /** @type {CustomerPondModel} Single customer pond model. */
  public customerPond: CustomerPondModel;

  /** @type {FeedLogModel} Feed log model for details. */
  public feedlogs: FeedLogModel;

  /** @type {boolean} Page loading state flag. */
  pgLoad = false;

  /** @type {string} Name of the selected pond. */
  pondName: string;

  // pagination sorting
  /** @type {number} Current page number. Default 1. */
  currentPageNumber: number = 1;

  /** @type {number} Items per page. Default 20. */
  noOfItemsPerPage: number = 20;

  /** @type {string} Logged-in user role. */
  public loginPersonType: string;

  /** @type {string} Logged-in user business name. */
  public loginUserBusinessName: string;

  /** @type {string} Logged-in user business category. */
  public loginUserBusinessCategory: string;

  /** @type {boolean} Sort flag for Farm Name. */
  pondfarmAsc: boolean;

  /** @type {any} Original list of ponds before filtering. */
  originalpondList: any;

  /** @type {boolean} Sort flag for Section Name. */
  pondsectionnameAsc: boolean;

  /** @type {boolean} Sort flag for Location Name. */
  pondlocationnameAsc: boolean;

  /** @type {boolean} Sort flag for Pond Name. */
  pondnameAsc: boolean;

  /** @type {string} Search keyword. */
  search: string;

  /** @type {boolean} List loader spinner flag. */
  public listLoader = false;              // Show listLoader spinner

  /** @type {number} Start index for batch loading. */
  public startIndex = 0;             // Tracks how much has been loaded

  /** @type {boolean} Flag indicating if filtering is active. */
  public isFiltering: boolean = false;

  /** @type {boolean} Flag indicating if list is empty. */
  public noList: boolean = false;

  /** @type {boolean} Flag to enable/disable page loader. */
  public enableDisablePageLoader: boolean = false;

  /** @type {any} Search debounce timeout. */
  searchTimeout: any;

  /** @type {BatchLoader<any>} Batch loader utility instance. */
  private batchLoader: BatchLoader<any>;

  /* End pagination, sorting, filter*/

  /**
   * Constructor for TotalPondsListComponent.
   *
   * @param {FarmSiteService} farmsiteEntityService - Service for farm site operations.
   * @param {CustomerPondService} customerPondService - Service for customer pond operations.
   * @param {FeedLogService} feedLogService - Service for feed logs.
   * @param {Sidenavaction} sidenavaction - Service for side navigation.
   * @param {CommonMethodsService} commonMethodsService - Service for common utility methods.
   * @param {LocationStrategy} locationStrategy - Service for interacting with the browser's URL.
   * @param {PreviousRouteService} previousRouteService - Service for tracking route history.
   */
  constructor(public farmsiteEntityService: FarmSiteService, public customerPondService: CustomerPondService, public feedLogService: FeedLogService, public sidenavaction: Sidenavaction, public commonMethodsService: CommonMethodsService,
              private locationStrategy: LocationStrategy, private previousRouteService: PreviousRouteService
  ) {
  }

  /**
   * @lifecycle ngOnInit
   * @async
   * @description
   * Initializes the component. Fetches farm sites and ponds based on user role.
   * Sets up the batch loader for pagination.
   */
  public async ngOnInit() {
    try {
      window.scrollTo(0, 0);
      this.isFiltering = false;
      this.customerPonds = [];
      this.customerPondsList = [];
      this.previousRouteService.setPreviousRoute(this.locationStrategy.path());
      // loginperson type details
      this.loginPersonType = localStorage.getItem('loginPersonType');
      // login person Business name
      this.loginUserBusinessName = localStorage.getItem('loginBusinessName');
      this.loginUserBusinessCategory = localStorage.getItem('loginBusinessCategory');
      let farmsiteIds = new Array();
      if ((this.loginPersonType == 'Farmer' || this.loginPersonType == 'Business Admin') && this.loginUserBusinessCategory == 'Farm') {
        let farmSiteModel = await this.farmsiteEntityService.getFarmeSitesByFarmsId(localStorage.getItem('businessID'));
        farmsiteIds = farmSiteModel.map(farmsite => farmsite.id);
        this.customerPonds = await this.customerPondService.getPondsByFarmsites(farmsiteIds);
        if (this.customerPonds !== undefined) {
          this.originalpondList = [...this.customerPonds];
          this.customerPonds = _.orderBy(this.customerPonds, ['createdAt'], ['desc']);
          this.pgLoad = true;
          this.customerPondsList = this.customerPonds;
        }
      } else if (this.loginPersonType == 'Farm Manager' && this.loginUserBusinessCategory == 'Farm') {
        let farmSiteModel = await this.farmsiteEntityService.getFarmeSitesByFarmManagerIdInFarmSpend(localStorage.getItem('businessID'), localStorage.getItem('personID'));
        farmsiteIds = farmSiteModel.map(farmsite => farmsite.id);
        this.customerPonds = await this.customerPondService.getPondsByFarmsites(farmsiteIds);
        if (this.customerPonds !== undefined) {
          this.originalpondList = [...this.customerPonds];
          this.customerPonds = _.orderBy(this.customerPonds, ['createdAt'], ['desc']);
          this.pgLoad = true;
          this.customerPondsList = this.customerPonds;
        }
      } else if (this.loginPersonType == 'Business Admin' && this.loginUserBusinessCategory == 'Provider') {
        let farmSiteModel = await this.farmsiteEntityService.getFarmeSitesBusinessDetailsByProviderId(localStorage.getItem('businessID'));
        farmsiteIds = farmSiteModel.map(farmsite => farmsite.id);
        this.customerPonds = await this.customerPondService.getPondsByFarmsites(farmsiteIds);
        if (this.customerPonds !== undefined) {
          this.originalpondList = [...this.customerPonds];
          this.customerPonds = _.orderBy(this.customerPonds, ['createdAt'], ['desc']);
          this.pgLoad = true;
          this.customerPondsList = this.customerPonds;
        }
      } else {
        let farmSiteModel = await this.farmsiteEntityService.getAllFarmSites();
        farmsiteIds = farmSiteModel.map(farmsite => farmsite.id);
        this.customerPonds = await this.customerPondService.getPondsByFarmsites(farmsiteIds);
        this.pgLoad = true;
        if (this.customerPonds !== undefined) {
          this.originalpondList = [...this.customerPonds];
          this.customerPonds = _.orderBy(this.customerPonds, ['createdAt'], ['desc']);
          this.customerPondsList = this.customerPonds;
        }
      }
      this.initializeBatchLoader(); // Reset instance
      this.batchLoader?.reset();     // Clear state
      await this.batchLoader?.loadNextBatch(true); // Load first batch
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
    }
  }

  /**
   * @method onScroll
   * @description Host listener for window scroll events. Triggers batch loading.
   * @lifecycle HostListener
   */
  @HostListener('window:scroll', [])
  onScroll(): void {
    this.checkScrollAndLoad();
  }

  /** @private @type {any} Scroll debounce timeout. */
  private scrollTimeout: any;

  /**
   * @method checkScrollAndLoad
   * @private
   * @description Checks if scroll position is near bottom and loads next batch.
   */
  private checkScrollAndLoad(): void {
    clearTimeout(this.scrollTimeout);
    this.scrollTimeout = setTimeout(() => {
      if ((window.innerHeight + window.scrollY) >= document.body.offsetHeight - 100 && !this.batchLoader?.isLoading()) {
        this.batchLoader?.loadNextBatch();
      }
    }, 50);
  }

  /**
   * @method initializeBatchLoader
   * @private
   * @description Initializes the `BatchLoader` utility.
   */
  private initializeBatchLoader(): void {
    this.batchLoader = new BatchLoader<any>(
      () => this.isFiltering ? this.customerPonds : this.customerPondsList,
      (updatedList) => {
        this.customerPonds = updatedList;
        this.noList = updatedList.length === 0;
      },
      undefined, // <-- omit or pass undefined
      (loading) => this.listLoader = loading
    );
  }

  /**
   * @method getAllTotalAbwfcrList
   * @async
   * @description Fetches detailed feed logs and metrics for a specific pond and opens modal.
   * @param {any} list - The pond object.
   * @param {string} pondName - The name of the pond.
   */
  async getAllTotalAbwfcrList(list: any, pondName) {
    try {
      const pondId = list.id;
      let cultureId = list && list.PondCultureData[0] && list.PondCultureData[0].id;
      this.pondName = pondName;
      if (pondId && cultureId) {
        $('#myModal').modal('show');
        this.feedlogs = await this.feedLogService.getPondsByFarmsites(pondId, cultureId);
      }
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
    }
  }

  /**
   * @method sortingTanks
   * @description Sorts the displayed list based on the specified type.
   * @param {string} type - The field to sort by ('farm', 'sectionname', 'pondname', 'locationname').
   */
  sortingTanks(type: string): void {
    try {
      // Always use the full list
      let sourceList = this.customerPondsList;

      // If search is active, filter first before sorting
      if (this.search && this.search.trim() !== '') {
        const searchValue = this.search.toLowerCase();
        sourceList = _.filter(this.customerPondsList, (obj) => {
          return (
            obj?.name?.toLowerCase().includes(searchValue) ||
            obj?.PondSection?.section_name?.toLowerCase().includes(searchValue) ||
            obj?.FarmSiteDetails?.[0]?.address?.village?.toLowerCase().includes(searchValue) ||
            obj?.FarmSiteDetails?.[0]?.businessID?.businessName?.toLowerCase().includes(searchValue)
          );
        });
      }

      const getValue = (item: any) => {
        switch (type) {
          case 'farm':
            return item?.FarmSiteDetails?.[0]?.businessID?.businessName || '';
          case 'sectionname':
            return item?.PondSection?.section_name || '';
          case 'pondname':
            return item?.name || '';
          case 'locationname':
            return item?.FarmSiteDetails?.[0]?.address?.village || '';
          default:
            return '';
        }
      };

      let orderDirection = 'asc';

      switch (type) {
        case 'farm':
          this.pondfarmAsc = !this.pondfarmAsc;
          orderDirection = this.pondfarmAsc ? 'asc' : 'desc';
          this.pondsectionnameAsc = this.pondlocationnameAsc = this.pondnameAsc = false;
          break;
        case 'sectionname':
          this.pondsectionnameAsc = !this.pondsectionnameAsc;
          orderDirection = this.pondsectionnameAsc ? 'asc' : 'desc';
          this.pondfarmAsc = this.pondlocationnameAsc = this.pondnameAsc = false;
          break;
        case 'pondname':
          this.pondnameAsc = !this.pondnameAsc;
          orderDirection = this.pondnameAsc ? 'asc' : 'desc';
          this.pondfarmAsc = this.pondlocationnameAsc = this.pondsectionnameAsc = false;
          break;
        case 'locationname':
          this.pondlocationnameAsc = !this.pondlocationnameAsc;
          orderDirection = this.pondlocationnameAsc ? 'asc' : 'desc';
          this.pondfarmAsc = this.pondnameAsc = this.pondsectionnameAsc = false;
          break;
      }

      // Apply sort to the filtered (or full) list
      const sortedList = [...sourceList].sort((a, b) => {
        const valA = getValue(a).toLowerCase();
        const valB = getValue(b).toLowerCase();
        if (valA < valB) return orderDirection === 'asc' ? -1 : 1;
        if (valA > valB) return orderDirection === 'asc' ? 1 : -1;
        return 0;
      });

      // Assign sorted list to full list & reset batch loader
      this.customerPondsList = sortedList;
      this.batchLoader?.reset();
      this.batchLoader?.loadNextBatch(true);

    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
    }
  }

  /**
   * @method searchPondList
   * @description Filters the pond list based on search input.
   * Resets batch loader with filtered list.
   */
  searchPondList() {
    try {
      if (!this.search || this.search.trim() === '') {
        this.customerPondsList = [...this.originalpondList];
      } else {
        const searchValue = this.search.toLowerCase();
        this.customerPondsList = _.filter(this.originalpondList, (obj) => {
          return (
            obj?.name?.toLowerCase().includes(searchValue) ||
            obj?.PondSection?.section_name?.toLowerCase().includes(searchValue) ||
            obj?.FarmSiteDetails?.[0]?.address?.village?.toLowerCase().includes(searchValue) ||
            obj?.FarmSiteDetails?.[0]?.businessID?.businessName?.toLowerCase().includes(searchValue)
          );
        });
      }
      // Reset and reload batch loader
      this.batchLoader?.reset();
      this.batchLoader?.loadNextBatch(true);

    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
    }
  }
}
